import { useMemo } from 'react';
import { useIsFetching } from '@tanstack/react-query';
import { INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY } from 'components/domain/customData/fields/internal/useFetchInternalCustomDataFields';
import { useTranslation } from 'react-i18next';
import useTable from 'components/table/hooks/useTable';
import { createColumnHelper } from '@tanstack/react-table';
import {
  DisplayPreArrivalFormField,
  InformationMap,
} from '../../PreArrivalFormTab.types';
import {
  CollapseSectionStyled,
  SectionTableStyled,
} from '../common/Common.styles';
import DisplayTypeCell from '../common/cells/DisplayTypeCell';
import DisplayNameCell from '../common/cells/DisplayNameCell';
import FieldValidationCell from './cells/FieldValidationCell';
import StatusCell from './cells/StatusCell';

const InformationSection = ({
  tableData,
  sectionHeader,
  id,
}: {
  tableData: InformationMap | undefined;
  sectionHeader: React.ReactNode;
  id: string;
}) => {
  const isFetchingData =
    useIsFetching({
      queryKey: [INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY],
    }) > 0;
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<DisplayPreArrivalFormField>();

  const columns = [
    columnHelper.display({
      id: 'status',
      cell: StatusCell,
      meta: {
        showCellTooltip: false,
      },
    }),
    columnHelper.display({
      id: 'displayName',
      header: t('pageAutomations.preArrivalForm.informationTable.fieldName'),
      cell: DisplayNameCell,
    }),
    columnHelper.display({
      id: 'displayType',
      header: t('pageAutomations.preArrivalForm.informationTable.fieldType'),
      cell: DisplayTypeCell,
    }),
    columnHelper.display({
      id: 'fieldValidation',
      header: t(
        'pageAutomations.preArrivalForm.informationTable.fieldValidation',
      ),
      meta: {
        showCellTooltip: false,
      },
      cell: FieldValidationCell,
    }),
  ];

  const tableDataArray = useMemo(
    () => Object.values(tableData || {}),
    [tableData],
  );

  const tableInstance = useTable<DisplayPreArrivalFormField>({
    columns,
    data: tableDataArray,
  });

  return (
    <CollapseSectionStyled
      variant="card"
      label={sectionHeader}
      defaultExpanded
      data-testid={`pre-arrival-form-section-${id}`}
    >
      <SectionTableStyled
        isLoading={isFetchingData}
        tableInstance={tableInstance}
      />
    </CollapseSectionStyled>
  );
};

export default InformationSection;
